import React, { useEffect, useMemo, useState } from 'react';
import { Text, Platform, Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { getBillingSummeryData } from '../../../api/Reservation';
import { getBrandsData } from '../../../api/Price';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHButton, BOHTlbCheckbox, BOHTlbrSearchPicker, BOHToolbar, renderBOHTlbDatePicker } from '../../../common/components/bohtoolbar';
import { BOHTBody, BOHTD, BOHTH, BOHTHead, BOHTR, BOHTable } from '../../../common/components/bohtable';
import { msgStr } from '../../../common/constants/Message';
import { TextdefaultSize } from '../../../common/constants/Fonts';
import { useAlertModal } from '../../../common/hooks';
import { formatDate, formatDate2 } from '../../../common/utils/DateUtils';
import { API_URL } from '../../../common/constants/AppConstants';

const ReservationsList = ({ navigation }) => {
  const { showAlert } = useAlertModal();
  const [tableData, setTableData] = useState([]);
  const [isloading, setIsLoading] = useState(true);
  const [updateReservationListTrigger, setUpdateReservationListTrigger] = useState(false);
  const [periodRange, setPeriodRange] = useState<any>('');
  const initialWidths = [100, 140, 200, 100];
  const [brandData, setBrandData] = useState([]);
  const [sorting, setSorting] = useState({
    key: null,
    direction: null,
  })

  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);

  const [ searchOptions, setSearchOptions ] = useState({
    start_date : `${twoWeeksAgo.getFullYear()}-${String(twoWeeksAgo.getMonth() + 1).padStart(2, '0')}-${String(twoWeeksAgo.getDate()).padStart(2, '0')}`,
    end_date : `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`,
    brand: '',
    // order_number: '',
    // stage: null,
    // status_filter: null,
    // hideBeachTennis: false,
    // ShowOnlyManual: false,
    includeBikes: true,
    includeNonBikes: true,
  });

  useEffect(()=>{
    loadSearchOption();
  }, [])

  const exportBillingSummery = async () => {
      try {
        const params = new URLSearchParams({
          start_date: searchOptions.start_date,
          end_date: searchOptions.end_date,
          brand: searchOptions.brand,
          // order_number: searchOptions.order_number,
          // stage: searchOptions.stage,
          // status_filter: searchOptions.status_filter,
          // hideBeachTennis: searchOptions.hideBeachTennis.toString(),
          // ShowOnlyManual: searchOptions.ShowOnlyManual.toString(),
          includeBikes: searchOptions.includeBikes.toString(),
          includeNonBikes: searchOptions.includeNonBikes.toString(),
        });
        
        const url = `${API_URL}/reservations/exportbillingsummary?${params}`;
        
        // Open URL in device browser
        const supported = await Linking.canOpenURL(url);
        if (supported) {
          await Linking.openURL(url);
        } else {
          showAlert('Error', 'Unable to open export URL');
        }
      } catch (error) {
        showAlert('Export Error', 'Failed to export forecasting data');
      }
  };

  const copyTableData = async () => {
    if (!tableData || tableData.length === 0) {
      showAlert('Notice', 'No table data to copy.');
      return;
    }
  
    try {
      // Headers matching visible columns
      const headers = ['Start Date', 'Customer', 'Address', 'Total'];
  
      const rows = tableData.map(item => [
        item.start_date ? formatDate2(new Date(`${item.start_date} 00:00:00`)) : '',
        item.full_name ?? '',
        item.address ?? '',
        item.undiscounted_total !== undefined ? `$${item.undiscounted_total.toFixed(2)}` : ''
      ]);
  
      const csvContent = [
        headers.join('\t'),  // Use tab for Google Sheets
        ...rows.map(row => row.join('\t'))
      ].join('\n');
  
      await navigator.clipboard.writeText(csvContent);
  
      showAlert('Copied', 'Table data copied to clipboard. Paste in Google Sheets or Excel.');
    } catch (error) {
      console.error(error);
      showAlert('Error', 'Failed to copy data to clipboard.');
    }
  };  
  
  const loadSearchOption = async () => {
    const [cachedSearchOptions, cachedTimestamp] = await Promise.all([
      AsyncStorage.getItem('__search_billingsummery_options'),
      AsyncStorage.getItem('__search_billingsummery_options_timestamp')
    ]);
    if (cachedTimestamp && cachedSearchOptions &&(new Date().getTime() - parseInt(cachedTimestamp, 10)) < 600000 ) {
      setSearchOptions(JSON.parse(cachedSearchOptions));
    } else {
      AsyncStorage.removeItem('__search_billingsummery_options');
    }
  }

  useEffect(()=>{
    const timeout = setTimeout(() => {
      AsyncStorage.setItem('__search_billingsummery_options', JSON.stringify(searchOptions))
      AsyncStorage.setItem('__search_billingsummery_options_timestamp', new Date().getTime().toString())
      setUpdateReservationListTrigger(true)
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchOptions])

  useEffect(() => {
    const today = new Date();
    switch (periodRange.toLowerCase()) {
      case 'today':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date()),
          end_date: formatDate(new Date()),
        });
        break;
      case 'tomorrow':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)),
          end_date: formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)),
        });
        break;
      case 'yesterday':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)),
          end_date: formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)),
        });
        break;
      case 'today+tomorrow':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date()),
          end_date: formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)),
        });
        break;
      case '7days':
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
          end_date: formatDate(new Date()),
        });
      break;
      case 'next fri':
        let nextFriday = new Date(today);
        nextFriday.setDate(nextFriday.getDate() + (5 + 7 - nextFriday.getDay()) % 7);
        if (nextFriday.getTime() === today.getTime()) {
          nextFriday.setDate(nextFriday.getDate() + 7);
        }
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextFriday),
          end_date: formatDate(nextFriday),
        });
        break;
      case 'next sat':
        let nextSaturday = new Date(today);
        nextSaturday.setDate(nextSaturday.getDate() + (6 + 7 - nextSaturday.getDay()) % 7);
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextSaturday),
          end_date: formatDate(nextSaturday),
        });
        break;
      case 'next sun':
        let nextSunday = new Date(today);
        nextSunday.setDate(nextSunday.getDate() + (7 + 7 - nextSunday.getDay()) % 7);
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextSunday),
          end_date: formatDate(nextSunday),
        });
        break;
      case 'next mon':
        let nextMonday = new Date(today);
        nextMonday.setDate(nextMonday.getDate() + (1 + 7 - nextMonday.getDay()) % 7);
        setSearchOptions({
          ...searchOptions,
          start_date: formatDate(nextMonday),
          end_date: formatDate(nextMonday),
        });
        break;
      default:
        break;
    }
  }, [periodRange]);

  useEffect(() => {
    if (searchOptions.start_date && searchOptions.end_date) {
      if (searchOptions.start_date === formatDate(new Date()) &&
          searchOptions.end_date === formatDate(new Date())) {
        if(periodRange != 'Today') setPeriodRange('Today');
      } else if (searchOptions.start_date === formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000))) {
        if(periodRange != 'Tomorrow') setPeriodRange('Tomorrow');
      } else if (searchOptions.start_date === formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000))) {
        if(periodRange != 'Yesterday') setPeriodRange('Yesterday');
      } else if (searchOptions.start_date === formatDate(new Date()) &&
                  searchOptions.end_date === formatDate(new Date(Date.now() + 1 * 24 * 60 * 60 * 1000))) {
        if(periodRange != 'Today+Tomorrow') setPeriodRange('Today+Tomorrow');
      } else if (searchOptions.start_date === formatDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) &&
                  searchOptions.end_date === formatDate(new Date())) {
        if(periodRange != '7days') setPeriodRange('7days');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 4 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Fri') setPeriodRange('Next Fri');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 5 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Sat') setPeriodRange('Next Sat');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 6 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Sun') setPeriodRange('Next Sun');
      } else if (searchOptions.start_date == searchOptions.end_date && new Date(searchOptions.start_date).getDay() === 0 
                && (new Date(searchOptions.start_date).getTime() - new Date().getTime())/86400000 < 7) {
        if(periodRange != 'Next Mon') setPeriodRange('Next Mon');
      } else {
        if(periodRange != 'custom') setPeriodRange('custom');
      }
    }
  }, [searchOptions.start_date, searchOptions.end_date]);

  const changeSearchOptions = (key, val) => {
    setSearchOptions(prevOptions => ({
      ...prevOptions,
      [key]: val
    }));
  }

  useEffect(() => {
    if (updateReservationListTrigger == true) getTable();
  }, [updateReservationListTrigger]);

  const getTable = () => {
    setIsLoading(true);
    getBillingSummeryData({searchOptions:searchOptions}, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateReservationListTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
    getBrandsData((jsonRes, status, error) => {
      switch (status) {
        case 200:
          const sortedData = jsonRes
          .filter((item: any) => item.brand != null)
          .sort((a: any, b: any) => a.brand.localeCompare(b.brand));
    
          setBrandData(sortedData || []);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const handleSort = (key:string) => {
    const sortsUpdate = {
      key: key,
      direction: sorting.key == key && sorting.direction == 'ascending' ? 'descending' : 'ascending'
    }
    setSorting(sortsUpdate)
  }

  const renderTableData = useMemo(() => {
        
    if(sorting.key) 
    tableData.sort((a, b) => {
      if (a[sorting.key] < b[sorting.key]) return sorting.direction === 'ascending' ? -1 : 1;
      if (a[sorting.key] > b[sorting.key]) return sorting.direction === 'ascending' ? 1 : -1;
      return 0;
    });

    const rows = [];
    if (tableData.length > 0) {
      tableData.map((item, index) => {
        let wIndex = 0;
        rows.push(
          <BOHTR key={index}>
            <BOHTD width={initialWidths[wIndex++]}>{item.start_date ? formatDate2(new Date(`${item.start_date} 00:00:00`)) : ''}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>{item.full_name}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]}>{item?.address??''}</BOHTD>
            <BOHTD width={initialWidths[wIndex++]} textAlign={'right'}>${item?.undiscounted_total?.toFixed(2)??''}</BOHTD>
          </BOHTR>
        );
      });
    } else {
      <></>;
    }

    setIsLoading(false);
    return <>{rows}</>;
  }, [tableData, sorting]);

  const tableElement = useMemo(()=>{
    let wIndex = 0;
    const headers = [
      // { key: "brand", label: "Brand" },
      { key: "start_date", label: "Start Date" },
      { key: "full_name", label: "Customer" },
      // { key: "end_date", label: "End Date" },
      { key: "address", label: "Address" },
      { key: "undiscounted_total", label: "Total" }
    ];

    return(<BOHTable isLoading={isloading}>
      <BOHTHead>
        <BOHTR>
          {headers.map(({ key, label }) => (
            <BOHTH
              key={key}
              width={initialWidths[wIndex++]}
              isSorted={sorting.key === key}
              direction={sorting.key === key ? sorting.direction : null}
              onClick={() => handleSort(key)}
            >
              {label}
            </BOHTH>
          ))}
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {renderTableData}
      </BOHTBody>
    </BOHTable>)}, [isloading, tableData, sorting])

  return (
    <BasicLayout
      navigation={navigation}
      screenName={'Billing Summery'}
    >
      <CommonContainer>
        <BOHToolbar style={{zIndex:100}}>
          <Text style={{marginRight:8, fontSize:TextdefaultSize}}>Start Date</Text>
          {Platform.OS == 'web' && 
            renderBOHTlbDatePicker(searchOptions.start_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('start_date', formattedDate);
          })}
          <Text style={{marginHorizontal:8, fontSize:TextdefaultSize}}>End Date</Text>
          {Platform.OS == 'web' && 
            renderBOHTlbDatePicker(searchOptions.end_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('end_date', formattedDate);
          })}

          <BOHTlbrSearchPicker
            width={125}
            items={[{label:'', value:''}, ...brandData.map((item, index)=>({'label':item.brand, 'value':item.brand}))]}
            label="Brand"
            selectedValue={searchOptions.brand || ''}
            onValueChange={val=>changeSearchOptions('brand', val)}/>  

          <Text style={{ marginLeft: 25, fontSize: TextdefaultSize }}>{`Total orders #:  `}{tableData?.length??0}</Text>

          <BOHButton
            style={{ marginLeft: 25 }}
            label='Copy'
            onPress={copyTableData}
          />
          <BOHButton
            style={{ marginLeft: 25 }}
            label='Export'
            onPress={exportBillingSummery}
          />
        </BOHToolbar>
        <BOHToolbar>
          <BOHTlbCheckbox
            label={'Include Bikes'}
            CheckboxProps={{ value:searchOptions.includeBikes }}
            onPress={()=>{
              changeSearchOptions('includeBikes', !searchOptions.includeBikes);
            }}
          />

          <BOHTlbCheckbox
            label={'Include non-bikes'}
            style={{marginLeft:10}}
            CheckboxProps={{ value:searchOptions.includeNonBikes }}
            onPress={()=>{
              changeSearchOptions('includeNonBikes', !searchOptions.includeNonBikes);
            }}
          />

          <Text style={{marginLeft:10, fontSize:TextdefaultSize}}>{`Total: `}${tableData?.reduce((acc, item) => acc + (item.undiscounted_total || 0), 0).toFixed(2)??0}</Text>
        </BOHToolbar>
        {tableElement}
      </CommonContainer>
    </BasicLayout>
  );
};

export default ReservationsList;
